import { useActsStore } from "../store/useActsStore"
import * as THREE from "three"
import { useEffect, useMemo, useRef } from "react"
import { useFrame, useThree } from "@react-three/fiber"
import { useAudioStore } from "../store/useAudioStore"
import { audioBus } from "./audioRegistry"

export default function Music() {
    const currentActId = useActsStore((s) => s.getCurrentActId())
    
    const transitionProgress = useActsStore((s) => {
        const id = s.getCurrentActId()
        const act = s.actsProgress[id]
        if (act?.phase !== "transition") return 0
        return act.phaseProgress ?? 0
    })

    const { camera } = useThree()
    const loader = useMemo(() => new THREE.AudioLoader(), [])
    const tracks = useMemo(
        () => ["/audio/music-one.mp3", "/audio/music-two.mp3", "/audio/music-three.mp3"],
        []
    )

    const soundsRef = useRef([])
    const loadedRef = useRef([])

    useEffect(() => {
        const listener = camera.getObjectByName("AudioListener")
        if (!listener) return

        let cancelled = false

        soundsRef.current = tracks.map(() => {
            const s = new THREE.Audio(listener)
            s.setLoop(true)
            s.setVolume(0)
            audioBus.register(s)
            return s
        })
        loadedRef.current = tracks.map(() => false)

        tracks.forEach((url, i) => {
            loader.load(
                url,
                (buffer) => {
                    if (cancelled) return
                    const s = soundsRef.current[i]
                    if (!s) return
                    s.setBuffer(buffer)
                    loadedRef.current[i] = true
                },
                undefined,
                () => {
                    loadedRef.current[i] = false
                }
            )
        })

        return () => {
            cancelled = true
            soundsRef.current.forEach((s) => {
                audioBus.unregister(s)
                s.stop()
                s.disconnect()
            })
            soundsRef.current = []
            loadedRef.current = []
        }
    }, [camera, tracks, loader])

    useFrame(() => {
        const { enabled, master } = useAudioStore.getState()
        const sounds = soundsRef.current
        if (!enabled || !sounds.length) return
        if (!loadedRef.current.every(Boolean)) return

        
        sounds.forEach((s) => {
            if (s.isPlaying) return
            const now = s.context.currentTime
            s.gain.gain.cancelScheduledValues(now)
            s.gain.gain.setValueAtTime(0, now)
            s.play()
        })

        const BASE = 0.1
        const p = transitionProgress
        const fromIndex = currentActId - 1
        const toIndex = currentActId

        const volumes = tracks.map(() => 0)

        const stableIndex = Math.min(Math.max(currentActId, 0), tracks.length - 1)
        volumes[stableIndex] = BASE

        if (
            currentActId >= 1 &&
            fromIndex >= 0 &&
            toIndex >= 0 &&
            fromIndex < tracks.length &&
            toIndex < tracks.length
        ) {
            const tOut = THREE.MathUtils.clamp(THREE.MathUtils.inverseLerp(0.0, 0.5, p), 0, 1)
            const tIn = THREE.MathUtils.clamp(THREE.MathUtils.inverseLerp(0.8, 1.0, p), 0, 1)

            volumes.fill(0)
            volumes[fromIndex] = THREE.MathUtils.lerp(BASE, 0, tOut)
            volumes[toIndex] = THREE.MathUtils.lerp(0, BASE, tIn)
        }

        for (let i = 0; i < sounds.length; i++) {
            sounds[i].setVolume((volumes[i] ?? 0) * master)
        }
    })

    return null
}
